import { MAX_UPLOAD_BYTES, UPLOAD_TYPES, type UploadContentType } from '@agentisai/shared-types';
import { formatArtifactSize } from './formatArtifactMeta';

/**
 * Thrown for a failure whose message is already the exact user-facing copy —
 * {@link describeError}'s generic-Error fallback would otherwise flatten it to
 * "Something went wrong." (useUploadArtifact reads this to bypass that).
 */
export class UploadUserError extends Error {}

export type ValidateUploadResult =
  { ok: true; contentType: UploadContentType } | { ok: false; message: string };

function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot).toLowerCase();
}

/** `"Markdown (.md) and PDF (.pdf)"` — every supported type, its label + primary extension. */
function supportedTypesList(): string {
  const parts = Object.values(UPLOAD_TYPES).map((t) => `${t.label} (${t.extensions[0]})`);
  return parts.length < 2
    ? (parts[0] ?? '')
    : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`;
}

/**
 * Phase G pre-flight check (design doc "the SPA check is not decoration") —
 * pure and unit-testable so it can run from any entry point (the file picker
 * today, drag-and-drop later) before any request is sent.
 *
 * Derives `contentType` from the filename's extension via {@link UPLOAD_TYPES}
 * — **never** `File.type` (the design doc's other trap: browsers commonly
 * report `""` or `text/plain` for `.md`, which would fail a perfectly valid
 * upload against the BFF allowlist and the exact-match S3 policy condition).
 *
 * Also checks {@link MAX_UPLOAD_BYTES} — the same cap S3's `content-length-range`
 * policy condition enforces (Phase D) — so an oversized file is rejected here
 * instead of uploading in full only to 403 at S3.
 */
export function validateUpload(file: File): ValidateUploadResult {
  const ext = extensionOf(file.name);
  const entry = (
    Object.entries(UPLOAD_TYPES) as [UploadContentType, (typeof UPLOAD_TYPES)[UploadContentType]][]
  ).find(([, def]) => (def.extensions as readonly string[]).includes(ext));
  if (!entry) {
    return {
      ok: false,
      message: `${file.name} is not a supported file type. Only ${supportedTypesList()} can be added.`,
    };
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return {
      ok: false,
      message: `${file.name} is too large (${formatArtifactSize(file.size)}). The maximum is ${formatArtifactSize(MAX_UPLOAD_BYTES)}.`,
    };
  }
  return { ok: true, contentType: entry[0] };
}
